/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { createProjectRepoMapping, syncProjectRepoMapping } from '../api';
import { useSession } from '../contexts/SessionContext';
import { useFeedback } from '../contexts/FeedbackContext';
import { REPO_LINK_PENDING_KEY } from './RepoMappingsCard';

type RepoLinkOauthCallbackPageProps = {
  verificationToken: string;
  error: string;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function RepoLinkOauthCallbackPage({
  verificationToken,
  error,
  onNavigate
}: RepoLinkOauthCallbackPageProps) {
  const { session } = useSession();
  const { toast } = useFeedback();
  const [status, setStatus] = useState<'working' | 'error'>('working');
  const [message, setMessage] = useState('Finishing GitHub authorization…');

  useEffect(() => {
    if (error) {
      setStatus('error');
      setMessage(error);
      return;
    }

    const raw = sessionStorage.getItem(REPO_LINK_PENDING_KEY);
    sessionStorage.removeItem(REPO_LINK_PENDING_KEY);

    if (!session || !verificationToken || !raw) {
      setStatus('error');
      setMessage(
        'Missing link request details. Please try linking the repo again.'
      );
      return;
    }

    const pending = JSON.parse(raw) as {
      externalRepo: string;
      projectIds?: string[];
    };

    createProjectRepoMapping(session.accessToken, {
      provider: 'github',
      externalRepo: pending.externalRepo,
      projectId: pending.projectIds?.[0],
      projectIds: pending.projectIds,
      verificationToken
    })
      .then(async (mapping) => {
        setMessage(
          'Repository linked. Importing delivery history and definition…'
        );
        try {
          const result = await syncProjectRepoMapping(
            session.accessToken,
            mapping.projectRepoMappingId
          );
          const definitionStatus = result.definition?.status || 'missing';
          toast(
            definitionStatus === 'invalid' ? 'error' : 'success',
            definitionStatus === 'activated'
              ? `Linked ${pending.externalRepo} and activated ${result.definition?.name}`
              : definitionStatus === 'invalid'
                ? `Linked ${pending.externalRepo}, but its .cindro/pipeline.yml is invalid`
                : `Linked ${pending.externalRepo}; no .cindro/pipeline.yml found`
          );
        } catch (syncError: any) {
          toast(
            'error',
            `Linked ${pending.externalRepo}, but its initial sync failed: ${syncError?.response?.data?.message || syncError?.message || 'unknown error'}`
          );
        }
        onNavigate('/', { replace: true });
      })
      .catch((err: any) => {
        setStatus('error');
        setMessage(
          err?.response?.data?.message || err?.message || 'Failed to link repo.'
        );
      });
  }, [error, session, verificationToken, toast, onNavigate]);

  return (
    <div className="authShell">
      <div className="authCard">
        <div className="authKicker">GitHub</div>
        <h1>
          {status === 'working' ? 'Linking repo…' : 'Could not link repo'}
        </h1>
        <p>{message}</p>
        {status === 'error' ? (
          <button
            type="button"
            className="primaryButton"
            onClick={() => onNavigate('/', { replace: true })}
          >
            Back to Cindro
          </button>
        ) : null}
      </div>
    </div>
  );
}

export { RepoLinkOauthCallbackPage };
